import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCrown,
  faMedal,
  faAward,
  faSearch,
  faCircleUser,
} from "@fortawesome/free-solid-svg-icons";
import { useLeagueMembers } from "../../hooks/league/useFetchLeagueMembers";
import { useUserById } from "../../hooks/user/useUserById";
import { useFetchUser } from "../../hooks/user/useFetchUser";
import VerificationBadge from "../user/VerificationBadge";
import Loading from "../common/Loading";

/**
 * GlobalLeaderboard - Mockup-style leaderboard with podium, search, and full rankings.
 * Replaces the old GlobalStandings component on the Global Competition page.
 */
const GlobalLeaderboard = () => {
  const globalLeagueId = process.env.REACT_APP_GLOBAL_LEAGUE_ID;
  const [page, setPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const limit = 10;

  const { data: dbUser } = useFetchUser();

  // Fetch top 3 for podium (always page 1, no search)
  const { data: topData, isLoading: isTopLoading } = useLeagueMembers(
    globalLeagueId,
    1,
    3,
    "",
  );

  // Fetch paginated list (with search support)
  const {
    data: leaderboardData,
    isLoading: isLeaderboardLoading,
    isFetching,
  } = useLeagueMembers(globalLeagueId, page, limit, searchQuery);

  const topThree = topData?.data || [];
  const rankings = leaderboardData?.data || [];
  const totalPlayers = leaderboardData?.total || 0;
  const totalPages = leaderboardData?.totalPages || 1;

  if (isTopLoading || (isLeaderboardLoading && page === 1 && !searchQuery)) {
    return <Loading fullScreen={false} />;
  }

  return (
    <div className="space-y-4">
      {/* Search */}
      <div className="relative">
        <FontAwesomeIcon
          icon={faSearch}
          className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-medium-grey"
        />
        <input
          type="text"
          placeholder="Search players..."
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
            setPage(1);
          }}
          className="w-full pl-10 pr-4 py-2.5 bg-white border border-gray-200 rounded-lg text-sm placeholder:text-medium-grey focus:outline-none focus:ring-2 focus:ring-deep-red/50 focus:border-deep-red/50 transition-all"
        />
      </div>

      {/* Top 3 Podium (only show when not searching) */}
      {!searchQuery && topThree.length >= 3 && (
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4">
          <h2 className="font-bold text-lg text-dark-grey mb-4">Top Players</h2>
          <div className="flex items-end justify-center gap-2">
            {/* 2nd Place */}
            <PodiumEntry
              userId={topThree[1]?.userId}
              user={topThree[1]?.user}
              points={topThree[1]?.points}
              position={2}
            />

            {/* 1st Place */}
            <PodiumEntry
              userId={topThree[0]?.userId}
              user={topThree[0]?.user}
              points={topThree[0]?.points}
              position={1}
            />

            {/* 3rd Place */}
            <PodiumEntry
              userId={topThree[2]?.userId}
              user={topThree[2]?.user}
              points={topThree[2]?.points}
              position={3}
            />
          </div>
        </div>
      )}

      {/* Full Rankings */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
          <h3 className="font-semibold text-dark-grey">Full Rankings</h3>
          <span className="text-xs text-medium-grey">
            {totalPlayers.toLocaleString()} players
          </span>
        </div>

        <div
          className={`divide-y divide-gray-100 transition-opacity duration-200 ${isFetching ? "opacity-50" : "opacity-100"}`}
        >
          {rankings.length > 0 ? (
            rankings.map((entry, index) => {
              const position = (page - 1) * limit + index + 1;
              return (
                <LeaderboardRow
                  key={entry.userId}
                  userId={entry.userId}
                  user={entry.user}
                  points={entry.points}
                  rank={position}
                  isCurrentUser={entry.userId === dbUser?._id}
                />
              );
            })
          ) : (
            <div className="px-4 py-8 text-center text-medium-grey text-sm">
              {searchQuery
                ? "No players found matching your search"
                : "No rankings available yet"}
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-4 py-3 border-t border-gray-100 flex items-center justify-between">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1 || isFetching}
              className="px-3 py-1.5 text-xs font-medium rounded-lg bg-gray-100 hover:bg-deep-red hover:text-white text-medium-grey transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <span className="text-xs text-medium-grey">
              Page {page} of {totalPages}
            </span>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages || isFetching}
              className="px-3 py-1.5 text-xs font-medium rounded-lg bg-gray-100 hover:bg-deep-red hover:text-white text-medium-grey transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * Podium entry for top 3 display
 */
const PodiumEntry = ({ userId, points, position, user: userProp }) => {
  const navigate = useNavigate();
  // Use the user embedded in the leaderboard payload; only fetch as a fallback.
  const { data: fetchedUser, isLoading: fetching } = useUserById(
    userProp ? undefined : userId,
  );
  const user = userProp || fetchedUser;
  const isLoading = !userProp && fetching;

  const displayName = user?.username || user?.name || "...";
  const formattedPoints = points ? points.toFixed(2) : "0.00";

  const getPositionStyles = () => {
    switch (position) {
      case 1:
        return {
          avatarSize: "w-[72px] h-[72px]",
          borderColor: "border-gold",
          bgColor: "bg-gold/20",
          iconColor: "text-gold",
          podiumHeight: "h-24",
          podiumBg: "bg-gold/20",
          textSize: "text-3xl",
          nameSize: "text-sm font-semibold",
          mt: "-mt-4",
        };
      case 2:
        return {
          avatarSize: "w-14 h-14",
          borderColor: "border-gray-400",
          bgColor: "bg-gray-200/50",
          iconColor: "text-gray-400",
          podiumHeight: "h-16",
          podiumBg: "bg-gray-200/50",
          textSize: "text-2xl",
          nameSize: "text-xs font-medium",
          mt: "",
        };
      case 3:
        return {
          avatarSize: "w-14 h-14",
          borderColor: "border-amber-700",
          bgColor: "bg-amber-100/50",
          iconColor: "text-amber-700",
          podiumHeight: "h-12",
          podiumBg: "bg-amber-100/50",
          textSize: "text-2xl",
          nameSize: "text-xs font-medium",
          mt: "",
        };
      default:
        return {};
    }
  };

  const styles = getPositionStyles();

  const getIcon = () => {
    switch (position) {
      case 1:
        return faCrown;
      case 2:
        return faMedal;
      case 3:
        return faAward;
      default:
        return faMedal;
    }
  };

  return (
    <div className={`flex flex-col items-center ${styles.mt}`}>
      {/* Avatar */}
      <div
        className={`${styles.avatarSize} rounded-full ${styles.bgColor} flex items-center justify-center mb-2 border-2 ${styles.borderColor} overflow-hidden cursor-pointer`}
        onClick={() => user?._id && navigate(`/users/${user._id}`)}
      >
        {isLoading ? (
          <div className="w-full h-full bg-gray-200 animate-pulse rounded-full" />
        ) : user?.picture ? (
          <img
            src={user.picture}
            alt={displayName}
            className="w-full h-full object-cover"
          />
        ) : (
          <FontAwesomeIcon
            icon={faCircleUser}
            className={`${position === 1 ? "w-8 h-8" : "w-6 h-6"} text-gray-400`}
          />
        )}
      </div>

      {/* Medal Icon */}
      <FontAwesomeIcon
        icon={getIcon()}
        className={`${position === 1 ? "w-6 h-6" : "w-5 h-5"} ${styles.iconColor} mb-1`}
      />

      {/* Name */}
      <span
        className={`${styles.nameSize} text-dark-grey truncate max-w-[70px] text-center cursor-pointer hover:text-deep-red`}
        onClick={() => user?._id && navigate(`/users/${user._id}`)}
      >
        {isLoading ? "..." : displayName}
      </span>

      {/* Points */}
      <span className="text-xs text-medium-grey">{formattedPoints} pts</span>

      {/* Podium Block */}
      <div
        className={`w-16 ${position === 1 ? "w-20" : "w-16"} ${styles.podiumHeight} ${styles.podiumBg} rounded-t-lg mt-2 flex items-center justify-center`}
      >
        <span className={`${styles.textSize} font-bold ${styles.iconColor}`}>
          {position}
        </span>
      </div>
    </div>
  );
};

/**
 * Individual leaderboard row
 */
const LeaderboardRow = ({ userId, points, rank, isCurrentUser, user: userProp }) => {
  const navigate = useNavigate();
  // Use the user embedded in the leaderboard payload; only fetch as a fallback.
  const { data: fetchedUser, isLoading: fetching } = useUserById(
    userProp ? undefined : userId,
  );
  const user = userProp || fetchedUser;
  const isLoading = !userProp && fetching;

  const displayName = user?.username || user?.name || "Loading...";
  const formattedPoints = points ? points.toFixed(2) : "0.00";

  return (
    <div
      className={`flex items-center gap-3 px-4 py-3 transition-colors ${
        isCurrentUser ? "bg-deep-red/5" : "hover:bg-gray-50"
      }`}
    >
      {/* Rank */}
      <div className="w-10 text-center">
        <span
          className={`font-bold text-sm ${
            rank <= 3
              ? "text-gold"
              : isCurrentUser
                ? "text-deep-red"
                : "text-medium-grey"
          }`}
        >
          #{rank}
        </span>
      </div>

      {/* Avatar & Name */}
      <div className="flex items-center gap-2 flex-1 min-w-0">
        <div
          className={`w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0 overflow-hidden ${
            isCurrentUser ? "ring-2 ring-deep-red" : ""
          }`}
        >
          {isLoading ? (
            <div className="w-full h-full bg-gray-200 animate-pulse rounded-full" />
          ) : user?.picture ? (
            <img
              src={user.picture}
              alt={displayName}
              className="w-full h-full object-cover"
            />
          ) : (
            <FontAwesomeIcon
              icon={faCircleUser}
              className="w-4 h-4 text-gray-400"
            />
          )}
        </div>
        <div className="min-w-0">
          <div className="flex items-center gap-1.5">
            <button
              onClick={() => user?._id && navigate(`/users/${user._id}`)}
              className={`font-medium text-sm truncate hover:underline cursor-pointer ${
                isCurrentUser ? "text-deep-red" : "text-dark-grey"
              }`}
            >
              {isLoading ? "..." : displayName}
            </button>
            {user?.verified && (
              <VerificationBadge
                verified={user.verified}
                size="small"
                showTooltip={false}
              />
            )}
          </div>
        </div>
      </div>

      {/* Points */}
      <div className="text-right flex-shrink-0">
        <p className="font-semibold text-sm text-dark-grey">
          {formattedPoints}
        </p>
        <p className="text-[10px] text-medium-grey">pts</p>
      </div>
    </div>
  );
};

export default GlobalLeaderboard;
